// ============================================================
// API Client — all backend calls in one place
// ============================================================

import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const client = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Auto-attach admin token if present
client.interceptors.request.use(config => {
  const token = localStorage.getItem('admin_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Public API ───────────────────────────────────────────────

export const getApartments = () =>
  client.get('/apartments').then(r => r.data);

export const getApartment = (id) =>
  client.get(`/apartments/${id}`).then(r => r.data);

export const getBookedDates = (apartmentId) =>
  client.get(`/apartments/${apartmentId}/booked-dates`).then(r => r.data);

export const checkAvailability = (data) =>
  client.post('/availability/check', data).then(r => r.data);

export const createBooking = (data) =>
  client.post('/bookings', data).then(r => r.data);

export const getBookingStatus = (ref) =>
  client.get(`/bookings/${ref}`).then(r => r.data);

// ── Chat (Guest) ─────────────────────────────────────────────

export const createChatSession = (landlordSlug, guestName, guestPhone) =>
  client.post('/chat/sessions', {
    landlord_slug: landlordSlug,
    guest_name: guestName,
    guest_phone: guestPhone,
  }).then(r => r.data);

export const sendGuestMessage = (sessionId, text) =>
  client.post(`/chat/${sessionId}/messages`, {
    text,
    direction: 'guest',
  }).then(r => r.data);

export const getChatMessages = (sessionId, afterId) =>
  client.get(`/chat/${sessionId}/messages`, {
    params: afterId ? { after: afterId } : {},
  }).then(r => r.data);

// ── Admin API ────────────────────────────────────────────────

export const adminLogin = (email, password) =>
  client.post('/admin/login', { email, password }).then(r => r.data);

export const getMe = () =>
  client.get('/admin/me').then(r => r.data);

export const getStats = () =>
  client.get('/admin/stats').then(r => r.data);

export const getAdminBookings = (params) =>
  client.get('/admin/bookings', { params }).then(r => r.data);

export const getAdminBooking = (id) =>
  client.get(`/admin/bookings/${id}`).then(r => r.data);

export const updateBooking = (id, data) =>
  client.patch(`/admin/bookings/${id}`, data).then(r => r.data);

export const getAdminApartments = () =>
  client.get('/admin/apartments').then(r => r.data);

export const createApartment = (data) =>
  client.post('/admin/apartments', data).then(r => r.data);

export const updateApartment = (id, data) =>
  client.patch(`/admin/apartments/${id}`, data).then(r => r.data);

export const deleteApartment = (id) =>
  client.delete(`/admin/apartments/${id}`).then(r => r.data);

// Upload photos
export const uploadPhotos = (files) => {
  const formData = new FormData();
  files.forEach(f => formData.append('photos', f));
  const token = localStorage.getItem('admin_token');
  return client.post('/uploads/photos', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  }).then(r => r.data.urls);
};

export const deletePhoto = (filename) => {
  return client.delete('/uploads/photos', {
    data: { filename },
  }).then(r => r.data);
};

// ── Chat (Landlord / Dashboard) ──────────────────────────────

export const getChatConversations = () =>
  client.get('/dashboard/chat/conversations').then(r => r.data);

export const getLandlordChatMessages = (sessionId) =>
  client.get(`/dashboard/chat/${sessionId}/messages`).then(r => r.data);

export const sendLandlordMessage = (sessionId, text) =>
  client.post(`/dashboard/chat/${sessionId}/messages`, { text }).then(r => r.data);

export const markChatRead = (sessionId) =>
  client.patch(`/dashboard/chat/${sessionId}/read`).then(r => r.data);

export const reorderApartments = (order) =>
  client.put('/admin/apartments/reorder', { order }).then(r => r.data);

export default client;
